import React, { useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import { X, ExternalLink, MapPin } from 'lucide-react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix Leaflet's default icon path issue with bundlers
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

// Parse a string like "48.8566, 2.3522" or "48.8566,2.3522" into [lat, lng]
export const parseCoordinates = (str) => {
  if (!str || typeof str !== 'string') return null;
  const m = str.trim().match(/^(-?\d{1,3}(?:\.\d+)?)\s*,\s*(-?\d{1,3}(?:\.\d+)?)$/);
  if (!m) return null;
  const lat = parseFloat(m[1]);
  const lng = parseFloat(m[2]);
  if (Number.isNaN(lat) || Number.isNaN(lng)) return null;
  if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return null;
  return [lat, lng];
};

const LocationMapModal = ({ open, onClose, lat, lng, label }) => {
  if (!open) return null;
  const gmaps = `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`;
  const osm = `https://www.openstreetmap.org/?mlat=${lat}&mlon=${lng}#map=17/${lat}/${lng}`;
  return (
    <div
      className="fixed inset-0 z-[1000] flex items-center justify-center bg-charcoal/60 backdrop-blur-sm px-4"
      onClick={onClose}
      data-testid="location-map-modal"
    >
      <div
        className="bg-white border-2 border-charcoal rounded-2xl shadow-brutal-xl max-w-2xl w-full overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-3 border-b-2 border-charcoal/10">
          <div className="flex items-center gap-2 min-w-0">
            <MapPin className="w-5 h-5 text-primary shrink-0" />
            <p className="font-heading font-bold truncate">
              {label || `${lat.toFixed(4)}, ${lng.toFixed(4)}`}
            </p>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-surface-alt rounded-lg" data-testid="location-map-close">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="h-[60vh] min-h-[320px]" data-testid="location-map-container">
          <MapContainer
            center={[lat, lng]}
            zoom={16}
            scrollWheelZoom
            style={{ height: '100%', width: '100%' }}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <Marker position={[lat, lng]}>
              <Popup>
                <strong>{label || 'Localisation partagee'}</strong>
                <br />
                {lat.toFixed(5)}, {lng.toFixed(5)}
              </Popup>
            </Marker>
          </MapContainer>
        </div>
        <div className="flex flex-wrap gap-2 p-3 border-t-2 border-charcoal/10 bg-surface-alt">
          <a
            href={gmaps}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-outline text-xs flex items-center gap-1 px-3 py-1.5"
            data-testid="location-map-gmaps"
          >
            <ExternalLink className="w-3 h-3" /> Google Maps
          </a>
          <a
            href={osm}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-outline text-xs flex items-center gap-1 px-3 py-1.5"
            data-testid="location-map-osm"
          >
            <ExternalLink className="w-3 h-3" /> OpenStreetMap
          </a>
        </div>
      </div>
    </div>
  );
};

// Helper component: renders a clickable location chip (or plain text if not coords)
export const LocationChip = ({ value, className = '' }) => {
  const [open, setOpen] = useState(false);
  const coords = parseCoordinates(value);
  if (!coords) {
    // Not parseable -> plain text
    return <span className={className}>{value}</span>;
  }
  const [lat, lng] = coords;
  return (
    <>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        className={`underline decoration-dotted hover:decoration-solid hover:text-primary cursor-pointer text-left ${className}`}
        data-testid="location-chip"
        title="Voir sur la carte"
      >
        {value}
      </button>
      <LocationMapModal open={open} onClose={() => setOpen(false)} lat={lat} lng={lng} label={value} />
    </>
  );
};

export default LocationMapModal;
